import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/shared/widgets/toast';
import { isImageSearchEnabled } from '@/core/utils/apiUtils';
import { searchDesignsByImage } from '../../application/services/ImageSearchService';
import { navigateToImageSearchPage } from '../../utils/searchNavigation';
import {
  normalizeImageSearchEmbeddingResult,
  writeImageSearchPayloadToSessionStorage,
} from '../../utils/imageSearchStorage';

const DEFAULT_ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'] as const;
const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024;

function getFileExtensionLower(name: string): string {
  const idx = name.lastIndexOf('.');
  if (idx === -1) return '';
  return name.slice(idx).toLowerCase();
}

export interface UseImageSearchOptions {
  enabled?: boolean;
  allowedExtensions?: readonly string[];
  maxFileSize?: number;
  onOpen?: () => void;
  /** Called before SPA navigation (e.g. close search modal/dropdown). */
  onSuccess?: () => void;
}

export function useImageSearch({
  enabled,
  allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  onOpen,
  onSuccess,
}: UseImageSearchOptions = {}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImageUploadOpen, setIsImageUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmittingImage, setIsSubmittingImage] = useState(false);

  const isEnabled = enabled ?? isImageSearchEnabled();

  const resetFileInput = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFile(null);
  }, []);

  const validate = useCallback(
    (f: File): string | null => {
      const ext = getFileExtensionLower(f.name);
      if (!allowedExtensions.includes(ext)) {
        return `Unsupported file type. Allowed: ${allowedExtensions.join(', ')}`;
      }
      if (f.size > maxFileSize) {
        return `File too large. Max size is ${Math.round(maxFileSize / (1024 * 1024))}MB.`;
      }
      return null;
    },
    [allowedExtensions, maxFileSize],
  );

  const openUploadPanel = useCallback(() => {
    setIsImageUploadOpen(true);
    setError(null);
    resetFileInput();
    onOpen?.();
  }, [onOpen, resetFileInput]);

  const handleFileChange = useCallback(
    (selected: File | null) => {
      setError(null);
      setFile(null);
      if (!selected) return;
      const err = validate(selected);
      if (err) {
        setError(err);
        return;
      }
      setFile(selected);
    },
    [validate],
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!file || isSubmittingImage) return;
      const err = validate(file);
      if (err) {
        setError(err);
        return;
      }

      setIsSubmittingImage(true);
      setError(null);
      try {
        const result = await searchDesignsByImage(file);
        const designIds = normalizeImageSearchEmbeddingResult(result);
        writeImageSearchPayloadToSessionStorage(designIds);
        resetFileInput();
        onSuccess?.();
        navigateToImageSearchPage(navigate);
      } catch (e2) {
        console.error('Image search failed', e2);
        resetFileInput();
        toast({
          variant: 'destructive',
          title: 'Image search failed',
          description: 'Unable to search by image right now. Please try again.',
        });
        setError('Upload failed. Please try again.');
      } finally {
        setIsSubmittingImage(false);
      }
    },
    [file, isSubmittingImage, validate, resetFileInput, onSuccess, navigate, toast],
  );

  const triggerFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    isEnabled,
    isImageUploadOpen,
    file,
    error,
    isSubmittingImage,
    fileInputRef,
    openUploadPanel,
    handleFileChange,
    handleSubmit,
    triggerFilePicker,
  };
}
